import i18n from 'src/locales/i18n';

export const LIMITE_ATIVAS = 8;
export const LIMITE_QUALIF = 6;

// Este arquivo exporta dados de módulo (CATALOGO e afins) consumidos fora de
// componentes React — PerguntasGlobaisCard.jsx e PerguntasNodeDrawer.jsx
// importam `CATALOGO` como array estático (`import { CATALOGO } from './catalog'`).
// Por isso o hook useTranslation() não pode ser usado aqui: não há como
// converter para um hook (`useCatalogo()`) sem também editar esses dois
// importadores, que estão fora do escopo desta tarefa. Em vez disso, usamos a
// instância singleton do i18next diretamente — mesmo padrão já usado em
// src/layouts/chats/NavConfig.js e src/utils/formatTime.js — o que mantém
// CATALOGO/CATALOGO_BY_ID/CATALOGO_BY_SPIN_FIELD/SPIN_CATALOG_IDS com o
// formato de sempre (array simples) e não exige nenhuma alteração nos
// importadores. Trade-off conhecido (o mesmo que NavConfig.js já tem hoje):
// os textos são resolvidos uma única vez, no carregamento do módulo — não
// reagem a uma troca de idioma em runtime sem reload da página.
const t = (key, defaultValue) => i18n.t(`settings:aiAgents.realstate.catalog.${key}`, defaultValue);

// Catálogo de perguntas disponíveis no fluxo de Perguntas Essenciais.
//
// Entradas com `spinField` preenchido são as 9 perguntas "essenciais SPIN"
// que a Lais sempre precisa responder antes de rodar a busca de imóveis.
// - `spinField`: chave usada pelo agente (lais-ai/src/agents/realestate/identity.js)
//   para mapear a pergunta ao campo_faltante do fluxo de qualificação.
// - `defaultScript`: texto padrão que a Lais usa para essa pergunta. O usuário
//   pode sobrescrever editando o campo `script` em cada instância da pergunta
//   dentro do workflow — o agente usa o script salvo (se houver), senão o default.
//
// ATENÇÃO: a lista de `spinField` precisa ficar sincronizada com
// lais-ai/src/agents/realestate/identity.js → CATALOG_TO_SPIN_FIELD.
// Alterar aqui exige alterar lá também (ou vice-versa).
export const CATALOGO = [
    // ─── Perguntas SPIN (essenciais pra busca) ────────────────────────
    {
        id: 'finalidade',
        titulo: t('finalidadeTitulo', 'Finalidade'),
        descricao: t('finalidadeDescricao', 'Morar, investir ou alugar.'),
        icon: 'mdi:bullseye-arrow',
        spinField: 'purpose',
        defaultScript: t('finalidadeScript', 'Voce quer comprar ou alugar?'),
    },
    {
        id: 'destination',
        titulo: t('destinationTitulo', 'Destino (residencial/comercial)'),
        descricao: t('destinationDescricao', 'Se o imóvel é para moradia ou uso comercial.'),
        icon: 'mdi:swap-horizontal',
        spinField: 'destination',
        defaultScript: t('destinationScript', 'Residencial ou comercial?'),
    },
    {
        id: 'tipo-imovel',
        titulo: t('tipoImovelTitulo', 'Tipo de imóvel'),
        descricao: t('tipoImovelDescricao', 'Apartamento, casa, sobrado, comercial, etc.'),
        icon: 'mdi:home-outline',
        spinField: 'type',
        defaultScript: t('tipoImovelScript', 'Qual tipo de imovel? (casa, apartamento, galpao, loja...)'),
    },
    {
        id: 'atividade',
        titulo: t('atividadeTitulo', 'Atividade (comercial)'),
        descricao: t('atividadeDescricao', 'Para que tipo de atividade o imóvel comercial será usado.'),
        icon: 'mdi:briefcase-outline',
        spinField: 'atividade',
        defaultScript: t('atividadeScript', 'Para qual tipo de atividade? (oficina, escritorio, loja...)'),
    },
    {
        id: 'regiao-interesse',
        titulo: t('regiaoInteresseTitulo', 'Região / Bairro de interesse'),
        descricao: t('regiaoInteresseDescricao', 'Bairros ou regiões preferidos.'),
        icon: 'mdi:map-marker-outline',
        spinField: 'bairro',
        defaultScript: t('regiaoInteresseScript', 'Tem preferencia de bairro ou regiao?'),
    },
    {
        id: 'metragem',
        titulo: t('metragemTitulo', 'Metragem mínima'),
        descricao: t('metragemDescricao', 'Área útil mínima desejada.'),
        icon: 'mdi:ruler-square',
        spinField: 'metragem',
        defaultScript: t('metragemScript', 'Precisa de metragem minima? (ex: 150m², 300m², 500m²)'),
    },
    {
        id: 'orcamento',
        titulo: t('orcamentoTitulo', 'Faixa de orçamento'),
        descricao: t('orcamentoDescricao', 'Valor máximo que o lead pode pagar.'),
        icon: 'mdi:cash-multiple',
        spinField: 'price',
        defaultScript: t('orcamentoScript', 'Qual valor esta disposto a pagar?'),
    },
    {
        id: 'quantidade-quartos',
        titulo: t('quantidadeQuartosTitulo', 'Quantidade de quartos'),
        descricao: t('quantidadeQuartosDescricao', 'Número mínimo de dormitórios.'),
        icon: 'mdi:bed-outline',
        spinField: 'quartos',
        defaultScript: t('quantidadeQuartosScript', 'Quantos quartos?'),
    },
    {
        id: 'vaga-garagem',
        titulo: t('vagaGaragemTitulo', 'Vaga de garagem'),
        descricao: t('vagaGaragemDescricao', 'Necessidade de vagas.'),
        icon: 'mdi:car-outline',
        spinField: 'vagas',
        defaultScript: t('vagaGaragemScript', 'Precisa de garagem?'),
    },

    // ─── Perguntas extras (enriquecimento CRM / locação / pós-busca) ───
    // Essas não têm spinField — não participam da qualificação para busca.
    // São coletadas como informação complementar para o corretor humano.
    { id: 'cpf', titulo: t('cpfTitulo', 'CPF no pré-atendimento'), descricao: t('cpfDescricao', 'Coletar CPF antes de enviar ao corretor (análise de crédito).'), icon: 'mdi:card-account-details-outline' },
    { id: 'tipo-garantia', titulo: t('tipoGarantiaTitulo', 'Tipo de garantia'), descricao: t('tipoGarantiaDescricao', 'Fiador, seguro fiança, caução ou depósito.'), icon: 'mdi:shield-key-outline' },
    { id: 'processo-locacao', titulo: t('processoLocacaoTitulo', 'Processo de locação'), descricao: t('processoLocacaoDescricao', 'Confirmar se o lead conhece o processo de locação.'), icon: 'mdi:clipboard-text-outline' },
    { id: 'prazo-mudanca', titulo: t('prazoMudancaTitulo', 'Prazo para mudança'), descricao: t('prazoMudancaDescricao', 'Quando o lead precisa se mudar.'), icon: 'mdi:calendar-clock' },
    { id: 'moradores', titulo: t('moradoresTitulo', 'Número de moradores'), descricao: t('moradoresDescricao', 'Quantas pessoas irão morar no imóvel.'), icon: 'mdi:account-group-outline' },
    { id: 'pets', titulo: t('petsTitulo', 'Possui pets'), descricao: t('petsDescricao', 'Se o lead tem animais (importante para locação).'), icon: 'mdi:paw-outline' },
    { id: 'bairro', titulo: t('bairroTitulo', 'Bairro específico'), descricao: t('bairroDescricao', 'Bairro específico de interesse (complementa "Região / Bairro").'), icon: 'mdi:map-outline' },
    { id: 'andar', titulo: t('andarTitulo', 'Preferência de andar'), descricao: t('andarDescricao', 'Andar alto, baixo ou indiferente.'), icon: 'mdi:office-building-outline' },
    {
        id: 'contato-preferencial',
        titulo: t('contatoPreferencialTitulo', 'Canal de contato'),
        descricao: t('contatoPreferencialDescricao', 'Canal preferido pelo cliente — enviado como "mídia" ao CRM parceiro.'),
        icon: 'mdi:phone-outline',
        spinField: 'canal_contato',
        opcoesConfiguraveis: true,
        // Espelha as opções de "Mídia de origem" do Imoview.
        // Pré-carregadas quando o admin ativa a pergunta pela primeira vez.
        // NÃO traduzir os `label` abaixo (propositalmente fora do t()): são
        // copiados verbatim para `aiAgent.config...opcoes` quando o admin
        // ativa a pergunta, e repassados ao Imoview como "mídia de origem" —
        // precisam bater exatamente com o vocabulário fixo em PT-BR que o
        // Imoview espera, independente do idioma do dashboard. `value` já
        // era um token interno e continua intocado.
        opcoesDefault: [
            { label: 'Facebook', value: 'facebook' },
            { label: 'Google', value: 'google' },
            { label: 'Indicação', value: 'indicacao' },
            { label: 'Instagram', value: 'instagram' },
            { label: 'Loja', value: 'loja' },
            { label: 'Migração de dados', value: 'migracao-de-dados' },
            { label: 'Placa', value: 'placa' },
            { label: 'Placas/Faixas', value: 'placas-faixas' },
            { label: 'RD Station', value: 'rd-station' },
            { label: 'Site', value: 'site' },
        ],
        defaultScript: t('contatoPreferencialScript', 'Para eu conseguir te atender melhor, me conta uma coisa: como você encontrou a gente? 😊'),
    },
];

export const CATALOGO_BY_ID = CATALOGO.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
}, {});

// Lookup por spinField: usado em componentes que precisam identificar qual
// pergunta do catálogo representa cada campo SPIN.
export const CATALOGO_BY_SPIN_FIELD = CATALOGO.reduce((acc, item) => {
    if (item.spinField) acc[item.spinField] = item;
    return acc;
}, {});

// IDs de perguntas que participam do fluxo SPIN. Útil para decidir onde
// mostrar o editor de script na UI (só faz sentido para SPIN).
export const SPIN_CATALOG_IDS = new Set(
    CATALOGO.filter((item) => item.spinField).map((item) => item.id)
);
